import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button, IconCollection } from '../../components';
import { useLocationBasedPhoneNumber } from '../../hooks/useLocationBasedPhoneNumber';
import { useConfiguration } from '../../context/configurationContext';
import {
  buildShareLinkMessage,
  getShareSenderName,
  getShareableUrl,
  shareOrCopyLink,
  SHARE_OUTCOME_COPIED,
} from '../../util/shareLink';
import css from './ListingPage.module.css';

const LINK_COPIED_FEEDBACK_MS = 2000;

// Top action bar (Call / Message Owner / Share), right column, above the booking panel.
// Call reuses the same location-based support number as OrderPanel's own Call button.
// Share sends the listing link with the "has shared a link with you" message above
// it (util/shareLink.js): native share sheet where there is one, otherwise the whole
// text is copied and the button reads "Link copied" for a moment.
const ListingActionBar = ({ className, onContactUser }) => {
  const intl = useIntl();
  const currentUser = useSelector(state => state.user.currentUser);
  const config = useConfiguration();
  const locationBasedPhoneNumber = useLocationBasedPhoneNumber(
    process.env.REACT_APP_TEAM_PHONE_NUMBER
  );
  const [linkCopied, setLinkCopied] = useState(false);
  const copiedTimerRef = useRef(null);
  useEffect(() => () => clearTimeout(copiedTimerRef.current), []);

  const handleCall = () => {
    if (typeof window !== 'undefined') {
      window.location.href = `tel:${locationBasedPhoneNumber}`;
    }
  };

  const handleShare = async () => {
    if (typeof window === 'undefined') return;
    const outcome = await shareOrCopyLink({
      title: intl.formatMessage({ id: 'ShareLink.listingTitle' }),
      message: buildShareLinkMessage(intl, {
        senderName: getShareSenderName(currentUser),
        siteName: config.marketplaceName,
      }),
      url: getShareableUrl(),
    });
    if (outcome === SHARE_OUTCOME_COPIED) {
      setLinkCopied(true);
      clearTimeout(copiedTimerRef.current);
      copiedTimerRef.current = setTimeout(() => setLinkCopied(false), LINK_COPIED_FEEDBACK_MS);
    }
  };

  return (
    <div className={classNames(css.listingActionBar, className)}>
      <Button
        icon={<IconCollection name="phoneFillIcon" />}
        arrow="left"
        variant="secondary-outline"
        onClick={handleCall}
        className={css.actionBarButton}
      >
        <FormattedMessage id="OrderPanel.callButtonText" />
      </Button>
      <Button
        icon={<IconCollection name="questionnaireLineIcon" />}
        arrow="left"
        variant="secondary-outline"
        onClick={() => onContactUser && onContactUser()}
        className={css.actionBarButton}
      >
        <FormattedMessage id="AboutOwner.messageOwner" />
      </Button>
      <Button
        icon={<IconCollection name="share_icon" />}
        arrow="left"
        variant="secondary-outline"
        onClick={handleShare}
        className={css.actionBarButton}
        aria-live="polite"
      >
        {linkCopied ? (
          <FormattedMessage id="ShareLink.linkCopied" />
        ) : (
          <FormattedMessage id="ListingPageHeader.shareListing" />
        )}
      </Button>
    </div>
  );
};

export default ListingActionBar;
